import React, { useState, useEffect } from "react";
import { useUser } from "./../../Home/UserContext";
import { getRoleFromRoleId } from "./../../../utils/roleHelper";
import DashboardNavigation from "./../../DashboardNavigation/DashboardNavigation";
import useScrollToTop from "./../../../hooks/useScollToTop";
import ScrollIndicator from "./../../Home/ScrollIndicator";
import { getAllEvents } from "./../../../services/eventServices";
import { getUsersEvents } from "./../../../services/usersEventsServices"; // Import des fonctions de gestion des favoris
import { getSettings } from "./../../../services/settingsServices"; // Import du service getSettings
import EventCardUser from "./EventCardUser";
import HeaderTitle from "../../../components/HeaderTitle/HeaderTitle";

const ListEvents = () => {
  useScrollToTop();
  const { user } = useUser();
  const userRole = getRoleFromRoleId(user?.role_id);
  const [events, setEvents] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterCategory, setFilterCategory] = useState("");
  const [filterType, setFilterType] = useState("");
  const [filterLevel, setFilterLevel] = useState("");
  const [filterStatus, setFilterStatus] = useState("A Venir");
  const [showFavorites, setShowFavorites] = useState(false); // État pour afficher les favoris
  const [favoriteEvents, setFavoriteEvents] = useState([]);
  const [shortnameClub, setShortnameClub] = useState("");

  // Récupérer userId depuis localStorage
  const getUserIdFromLocalStorage = () => {
    try {
      const userInfo = JSON.parse(localStorage.getItem("userInfo"));
      return userInfo?.userId;
    } catch (error) {
      console.error(
        "Erreur lors de la récupération de userId depuis localStorage:",
        error
      );
      return null;
    }
  };

  const userId = getUserIdFromLocalStorage();

  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const data = await getSettings();
        if (data.length > 0) {
          setShortnameClub(data[0].shortname_club);
        }
      } catch (error) {
        console.error("Erreur lors de la récupération des paramètres", error);
      }
    };

    const fetchEvents = async () => {
      try {
        const data = await getAllEvents();
        setEvents(data.filter((event) => event.is_active)); // Filtrer pour garder seulement les événements actifs

        if (userId) {
          // Charger les événements favoris
          const favorites = await getUsersEvents(userId);
          setFavoriteEvents(favorites.map((fav) => fav.event_id));
        } else {
          console.error("User ID is undefined");
        }
      } catch (error) {
        console.error("Erreur lors du chargement des événements", error);
      }
    };

    fetchSettings();
    fetchEvents();
  }, [userId]);

  // Liste des types d'événements (à ajuster selon vos données)
  const typeEvents = [
    shortnameClub || "Votre Club",
    "Tournoi Interclubs",
    "Cours commun club",
    "Stage Club",
    "Compétition",
    "Animation",
    "Stage & Regroupement Technique",
    "Passage de Grade",
    "Entraînement Départemental",
    "Stage Sportif Départemental",
    "Autre",
  ];

  // Liste des niveaux d'événements (à ajuster selon vos données)
  const levelEvents = [
    "Club",
    "Départemental",
    "Régional",
    "National",
    "International",
  ];

  // Liste des catégories d'événements
  const cateEvents = [
    "Mini-Poussin(e)",
    "Poussin(e)",
    "Pupille",
    "Benjamin(e)",
    "Minime",
    "Cadet(te)",
    "Junior",
    "Sénior",
    "Vétéran",
  ];

  // Définir les statuts basés sur la date
  const statusEvents = ["A Venir", "Passé"];

  const filteredEvents = events.filter((event) => {
  if (!event) return false;

  const safeLower = (v) => (v ?? "").toString().toLowerCase();

  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const eventStartDate = new Date(event.start);
  if (isNaN(eventStartDate)) return false; // date invalide => on ignore
  eventStartDate.setHours(0, 0, 0, 0);

  const isPast = eventStartDate < today;
  const isTodayOrFuture = eventStartDate >= today;

  const statusFilter =
    filterStatus === "A Venir"
      ? isTodayOrFuture
      : filterStatus === "Passé"
      ? isPast
      : true;

  // caté: split uniquement si string non vide
  const categories = typeof event.cate_event === "string" && event.cate_event.trim()
    ? event.cate_event.split(",").map((s) => s.trim())
    : [];

  const categoryFilter = filterCategory ? categories.includes(filterCategory) : true;

  const titleMatch = safeLower(event.titre).includes(safeLower(searchTerm));

  const isFavoriteEvent = showFavorites
    ? favoriteEvents.includes(event.id)
    : true;

  return (
    titleMatch &&
    categoryFilter &&
    (filterType ? event.type_event === filterType : true) &&
    (filterLevel ? event.level_event === filterLevel : true) &&
    statusFilter &&
    isFavoriteEvent &&
    event.is_active !== false // on garde true/undefined, on écarte false
  );
});


  return (
    <section className="w-full">
      <div className="pt-[150px]" aria-hidden />

      <div className="mx-auto w-full max-w-7xl px-3 sm:px-6">
        <HeaderTitle
          role="Utilisateur"
          title="Calendrier des événements"
          align="center"
          className="mb-1"
        />

        <div className="mb-6">
          <DashboardNavigation role={userRole} />
        </div>

        {/* Carte principale */}
        <div className="rounded-2xl overflow-hidden shadow ring-1 ring-slate-200/70 dark:ring-slate-700/70 bg-white/80 dark:bg-slate-900/60 backdrop-blur">
          {/* Header gradient */}
          <div className="px-4 py-5 sm:px-6 bg-gradient-to-r from-sky-500 via-violet-500 to-orange-400 text-white mb-4">
            <div className="grid gap-3 md:grid-cols-1">
              <div>
                <h3 className="text-lg sm:text-xl font-semibold">
                  Calendrier des événements
                </h3>
                <p className="text-white/90 text-sm">
                  Sur cette page, vous pouvez pré-inscrire ou désinscrire votre
                  adhérent à un événement.
                  <br />
                  Vous pouvez filtrer les événements par catégorie, type, niveau
                  et recherche par titre.
                  <br />
                  Vous pouvez également afficher uniquement les événements
                  favoris.
                  <br />
                  Légende : 🔲 Pré-inscrit / ✅ Validation par l'enseignant / 🥋
                  Coach présent
                </p>
              </div>
            </div>
          </div>

          <div className="flex flex-wrap justify-center items-middle mb-4 gap-4 mx-auto p-4">
            <input
              type="text"
              placeholder="Recherche par titre"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="input input-bordered w-full"
            />
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 w-full">
              <select
                value={filterCategory}
                onChange={(e) => setFilterCategory(e.target.value)}
                className="select select-bordered w-full dark:text-slate-800"
              >
                <option value="">Toutes catégories</option>
                {cateEvents.map((cate) => (
                  <option key={cate} value={cate}>
                    {cate}
                  </option>
                ))}
              </select>
              <select
                value={filterType}
                onChange={(e) => setFilterType(e.target.value)}
                className="select select-bordered w-full dark:text-slate-800"
              >
                <option value="">Tous types</option>
                {typeEvents.map((type) => (
                  <option key={type} value={type}>
                    {type}
                  </option>
                ))}
              </select>
              <select
                value={filterLevel}
                onChange={(e) => setFilterLevel(e.target.value)}
                className="select select-bordered w-full dark:text-slate-800"
              >
                <option value="">Tous niveaux</option>
                {levelEvents.map((level) => (
                  <option key={level} value={level}>
                    {level}
                  </option>
                ))}
              </select>
              <select
                value={filterStatus}
                onChange={(e) => setFilterStatus(e.target.value)}
                className="select select-bordered w-full dark:text-slate-800"
              >
                <option value="">Tous statuts</option>
                {statusEvents.map((status) => (
                  <option key={status} value={status}>
                    {status}
                  </option>
                ))}
              </select>
            </div>
            <label className="label cursor-pointer space-x-2">
              <span className="label-text dark:text-slate-200">
                Voir uniquement les favoris
              </span>
              <input
                type="checkbox"
                checked={showFavorites}
                onChange={() => setShowFavorites(!showFavorites)}
                className="toggle toggle-primary ml-2"
              />
            </label>
          </div>

          <div className="p-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 max-w-[1280px] mx-auto">
            {filteredEvents.map((event) => (
              <EventCardUser key={event.id} event={event} userId={userId} />
            ))}
          </div>
        </div>
      </div>
      <ScrollIndicator />
    </section>
  );
};

export default ListEvents;
